import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  Tabs, 
  Tab, 
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Divider,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  AccessTime as AccessTimeIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { format, addDays, startOfWeek, endOfWeek, eachDayOfInterval, isWithinInterval } from 'date-fns';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';

// Add these type declarations at the top of the file before the React import
declare global {
  interface Window {
    google: {
      maps: {
        Map: any;
        Marker: any;
        InfoWindow: any;
        LatLngBounds: any;
      };
    };
  }
}

// Fix Leaflet's default icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`hr-tabpanel-${index}`}
      aria-labelledby={`hr-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `hr-tab-${index}`,
    'aria-controls': `hr-tabpanel-${index}`,
  };
}

interface User {
  userId: string;  // Primary ID from backend
  id?: string;     // Add optional id for backward compatibility
  username?: string;
  first_name?: string;  // Keep this for backward compatibility
  last_name?: string;   // Keep this for backward compatibility
  firstName: string;    // Add proper backend field
  lastName: string;     // Add proper backend field
  email: string;
  job_title?: string;   // Optional field we use in UI
  phone_number?: string; // Optional field we use in UI
  department?: string;  // Optional field
  profile_image?: string;
  status?: 'active' | 'inactive' | 'on_leave';
  role?: string;
  locationName?: string;
  isActive?: boolean;
  lastLoginAt?: string;
  createdAt?: string;
  businessName?: string;
}

interface Shift {
  id: string;
  user_id: string;
  start_time: string;
  end_time: string;
  location: string;
  location_id?: string;
  notes?: string;
  assignments?: Array<{
    assignment_id: string;
    shift_id: string;
    user_id: string;
    role: string | null;
    assigned_by: string;
    created_at: string;
    updated_at: string;
  }>;
}

interface BusinessGroup {
  groupId: string;
  groupName: string;
  description?: string;
  createdAt?: string;
  teamLeaderId?: string;
  teamLeaderFirstName?: string;
  teamLeaderLastName?: string;
  teamLeaderEmail?: string;
  memberCount?: number;
}

interface TeamMember {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role?: string;
  addedAt?: string;
}

// Add an interface for locations
interface Location {
  locationId: string;
  locationName: string;
  address?: string;
  contactPerson?: string;
  contactPhone?: string;
}

// Add after the Location interface
interface TimeEntry {
  entry_id: string;
  user_id: string;
  business_id: string;
  shift_assignment_id?: string;
  clock_in: string;
  clock_in_location: string;
  clock_in_coordinates_lat?: number;
  clock_in_coordinates_lng?: number;
  clock_out?: string;
  clock_out_location?: string;
  clock_out_coordinates_lat?: number;
  clock_out_coordinates_lng?: number;
  notes?: string;
  status: string;
  created_at: string;
  updated_at: string;
  // Add user info for display purposes
  user_first_name?: string;
  user_last_name?: string;
  user_email?: string;
}

// Create a separate, memoized component for the map to prevent re-renders
const LocationMap = React.memo(({ locations }: { 
  locations: Array<{
    userId: string;
    firstName: string;
    lastName: string;
    validCoordinates: { lat: number; lng: number; isValid: boolean };
    lastUpdated: string;
    locationType?: 'clockIn' | 'clockOut';
  }>
}) => {
  // Use a simple type definition for coordinates instead of LatLngExpression
  const mapCenter: [number, number] = locations.length > 0 
    ? [locations[0].validCoordinates.lat, locations[0].validCoordinates.lng]
    : [51.505, -0.09]; // Default center (London)

  return (
    <MapContainer 
      // @ts-ignore - Types are incorrectly defined in react-leaflet
      center={mapCenter} 
      zoom={13} 
      scrollWheelZoom={true}
      style={{ height: '100%', width: '100%', zIndex: 1 }}
    >
      <TileLayer
        // @ts-ignore - Types are incorrectly defined in react-leaflet
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      
      {locations.map((user, index) => {
        // Create a custom marker icon based on location type
        const markerIcon = user.locationType === 'clockOut' 
          ? new L.Icon({
              iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
              shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
              iconSize: [25, 41],
              iconAnchor: [12, 41],
              popupAnchor: [1, -34],
              shadowSize: [41, 41]
            })
          : new L.Icon({
              iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
              shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
              iconSize: [25, 41],
              iconAnchor: [12, 41],
              popupAnchor: [1, -34],
              shadowSize: [41, 41]
            });
            
        return (
          <Marker 
            key={`${user.userId}-${index}-${user.locationType || 'location'}`}
            position={[user.validCoordinates.lat, user.validCoordinates.lng] as [number, number]}
            // @ts-ignore - Types are incorrectly defined in react-leaflet
            icon={markerIcon}
          >
            <Popup 
              // @ts-ignore - Types are incorrectly defined in react-leaflet
              minWidth={200} 
              maxWidth={300}
            >
              <Box sx={{ p: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {user.firstName} {user.lastName}
                </Typography>
                <Typography variant="body2" sx={{ 
                  mb: 0.5, 
                  color: user.locationType === 'clockOut' ? 'error.main' : 'success.main',
                  fontWeight: 'medium'
                }}>
                  {user.locationType === 'clockOut' ? 'Clock Out' : 'Clock In'} Location
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Time: {format(new Date(user.lastUpdated), 'dd/MM/yyyy HH:mm')}
                </Typography>
                <Typography variant="body2">
                  Coordinates: {user.validCoordinates.lat.toFixed(6)}, {user.validCoordinates.lng.toFixed(6)}
                </Typography>
              </Box>
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
  );
});

const HR: React.FC = () => {
  const [tabValue, setValue] = useState(0);
  const [staff, setStaff] = useState<User[]>([]);
  const [filteredStaff, setFilteredStaff] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Business permission state for HR module access
  const [allowHrModule, setAllowHrModule] = useState<boolean>(false);
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  
  // Staff edit dialog
  const [openStaffDialog, setOpenStaffDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [department, setDepartment] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive' | 'on_leave'>('active');
  
  // Rota state
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<Date>(new Date());
  const [openShiftDialog, setOpenShiftDialog] = useState(false);
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
  const [shiftUserId, setShiftUserId] = useState('');
  const [shiftStartTime, setShiftStartTime] = useState<Date | null>(null);
  const [shiftEndTime, setShiftEndTime] = useState<Date | null>(null);
  const [shiftLocation, setShiftLocation] = useState('');
  const [shiftNotes, setShiftNotes] = useState('');
  
  // Add locations state
  const [locations, setLocations] = useState<Location[]>([]);
  
  // Department/Team filtering for rota
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [departments, setDepartments] = useState<string[]>([]);

  // New state for teams
  const [teams, setTeams] = useState<BusinessGroup[]>([]);
  const [teamMembers, setTeamMembers] = useState<Record<string, TeamMember[]>>({});
  const [selectedTeam, setSelectedTeam] = useState<string>('all');

  // Add new state for time entries and map
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [timeEntriesLoading, setTimeEntriesLoading] = useState(false);
  const [timeEntriesError, setTimeEntriesError] = useState<string | null>(null);
  const [activeUsers, setActiveUsers] = useState<{
    userId: string;
    firstName: string;
    lastName: string;
    coordinates: { lat: number; lng: number };
    lastUpdated: string;
  }[]>([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [timeEntryDateRange, setTimeEntryDateRange] = useState<{
    startDate: Date;
    endDate: Date;
  }>({
    startDate: startOfWeek(new Date()),
    endDate: endOfWeek(new Date())
  });

  // Wrap fetchShifts in useCallback
  const fetchShifts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }

      const weekStart = format(startOfWeek(selectedWeek), 'yyyy-MM-dd');
      const weekEnd = format(endOfWeek(selectedWeek), 'yyyy-MM-dd');

      const response = await axios.get(`${API_BASE_URL}/shifts?start_date=${weekStart}&end_date=${weekEnd}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Process the shifts data to ensure it matches our frontend model
      const processedShifts = response.data.map((shift: any) => {
        // If there are assignments, set the first user_id for backward compatibility
        let user_id = '';
        if (shift.assignments && shift.assignments.length > 0) {
          user_id = shift.assignments[0].user_id;
        }
        
        return {
          id: shift.shift_id,
          user_id,
          start_time: shift.start_time,
          end_time: shift.end_time,
          location: shift.location_name || '',
          location_id: shift.location_id || '',
          notes: shift.notes || '',
          assignments: shift.assignments || []
        };
      });
      
      setShifts(processedShifts);
    } catch (err: any) {
      console.error('Error fetching shifts:', err);
      setError('Failed to load rota schedule. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [selectedWeek]);

  // Function to fetch locations
  const fetchLocations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle different response formats
      let locationData = [];
      
      // Check if response.data is an object with a data property
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        locationData = response.data.data.map((item: any) => ({
          locationId: item.location_id,
          locationName: item.location_name,
          address: item.address || '',
          contactPerson: item.contact_person || '',
          contactPhone: item.contact_phone || ''
        }));
      } 
      // Check if response.data is directly an array
      else if (Array.isArray(response.data)) {
        locationData = response.data;
      }
      else {
        console.error('Unexpected response format:', response.data);
        locationData = [];
      }
      
      setLocations(locationData);
    } catch (err: any) {
      console.error('Error fetching locations:', err);
    }
  }, []);

  // Add function to fetch time entries with enhanced user info
  const fetchTimeEntries = useCallback(async () => {
    try {
      setTimeEntriesLoading(true);
      setTimeEntriesError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }

      const startDateStr = format(timeEntryDateRange.startDate, 'yyyy-MM-dd');
      const endDateStr = format(timeEntryDateRange.endDate, 'yyyy-MM-dd');

      const response = await axios.get(
        `${API_BASE_URL}/time-entries?start_date=${startDateStr}&end_date=${endDateStr}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // Get the time entries
      const entries = Array.isArray(response.data) ? response.data : [];
      
      // If we have entries but no user info, get user details
      const entriesWithMissingNames = entries.filter(entry => 
        !entry.user_first_name || !entry.user_last_name
      );
      
      if (entriesWithMissingNames.length > 0) {
        try {
          // Fetch user details
          const usersResponse = await axios.get(`${API_BASE_URL}/users`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (Array.isArray(usersResponse.data)) {
            const userMap = usersResponse.data.reduce((acc, user) => {
              const userId = user.userId || user.id;
              if (userId) {
                acc[userId] = {
                  firstName: user.firstName || user.first_name || '',
                  lastName: user.lastName || user.last_name || '',
                  email: user.email || ''
                };
              }
              return acc;
            }, {});
            
            // Update entries with user info
            const enhancedEntries = entries.map(entry => {
              const userInfo = userMap[entry.user_id];
              if (userInfo && (!entry.user_first_name || !entry.user_last_name)) {
                return {
                  ...entry,
                  user_first_name: entry.user_first_name || userInfo.firstName,
                  user_last_name: entry.user_last_name || userInfo.lastName,
                  user_email: entry.user_email || userInfo.email
                };
              }
              return entry;
            });
            
            setTimeEntries(enhancedEntries);
            return;
          }
        } catch (err) {
          console.error('Error fetching user details:', err);
        }
      }
      
      setTimeEntries(entries);
    } catch (err: any) {
      console.error('Error fetching time entries:', err);
      setTimeEntriesError('Failed to load time entries. Please try again later.');
    } finally {
      setTimeEntriesLoading(false);
    }
  }, [timeEntryDateRange]);

  // Add function to fetch active users for the map
  const fetchActiveUsers = useCallback(async () => {
    try {
      setMapLoading(true);
      setMapError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }

      // First try the standard endpoint
      let response;
      try {
        response = await axios.get(
          `${API_BASE_URL}/time-entries/active`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
      } catch (e) {
        console.log('Failed to fetch from /time-entries/active endpoint, trying alternative');
        // If the /active endpoint fails, fetch all time entries with status=active
        response = await axios.get(
          `${API_BASE_URL}/time-entries?status=active`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
      }
      
      // Handle different response formats
      let activeEntries = [];
      
      // Check if response.data is an object with a data property
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        activeEntries = response.data.data;
      } 
      // Check if response.data is directly an array
      else if (Array.isArray(response.data)) {
        activeEntries = response.data;
      }
      else {
        console.error('Unexpected response format:', response.data);
        setMapError('Unexpected API response format. Please check console.');
        return;
      }
      
      console.log('Active entries:', activeEntries);
      
      if (activeEntries.length === 0) {
        setActiveUsers([]);
        return;
      }
      
      let userDetails: Record<string, { firstName: string; lastName: string }> = {};
      
      try {
        const usersResponse = await axios.get(
          `${API_BASE_URL}/users`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        if (Array.isArray(usersResponse.data)) {
          userDetails = usersResponse.data.reduce((acc: Record<string, { firstName: string; lastName: string }>, user: any) => {
            if (user.userId) {
              acc[user.userId] = {
                firstName: user.firstName || user.first_name || 'Unknown',
                lastName: user.lastName || user.last_name || 'User'
              };
            }
            return acc;
          }, {});
        }
      } catch (e) {
        console.error('Error fetching user details:', e);
      }

      // Group entries by user ID and find the most recent entry for each user
      const latestEntriesByUser: Record<string, any> = {};
      
      activeEntries.forEach((entry: any) => {
        const userId = entry.user_id || entry.userId || '';
        if (!userId) return;
        
        // Get the timestamp
        const entryTimestamp = entry.clock_in || entry.clockIn || entry.created_at || entry.createdAt || '';
        if (!entryTimestamp) return;
        
        const entryDate = new Date(entryTimestamp);
        
        // If we don't have an entry for this user yet, or this entry is more recent than the one we have
        if (!latestEntriesByUser[userId] || entryDate > new Date(latestEntriesByUser[userId].timestamp)) {
          latestEntriesByUser[userId] = {
            ...entry,
            timestamp: entryTimestamp
          };
        }
      });
      
      // Map response data to activeUsers format using only the latest entry for each user
      const mappedUsers = Object.values(latestEntriesByUser).map((entry: any) => {
        // Try different possible field names
        const userId = entry.user_id || entry.userId || `unknown-${Math.random().toString(36).substr(2, 9)}`;
        const user = userDetails[userId] || {};
        
        // Try different possible coordinate field names and ensure they're numbers
        let lat: number = 0;
        let lng: number = 0;
        
        // Check all possible latitude fields
        if (entry.clock_in_coordinates_lat !== undefined && entry.clock_in_coordinates_lat !== null) {
          lat = parseFloat(String(entry.clock_in_coordinates_lat));
        } else if (entry.clockInCoordinatesLat !== undefined && entry.clockInCoordinatesLat !== null) {
          lat = parseFloat(String(entry.clockInCoordinatesLat));
        } else if (entry.coordinates && entry.coordinates.lat !== undefined && entry.coordinates.lat !== null) {
          lat = parseFloat(String(entry.coordinates.lat));
        }
        
        // Check all possible longitude fields
        if (entry.clock_in_coordinates_lng !== undefined && entry.clock_in_coordinates_lng !== null) {
          lng = parseFloat(String(entry.clock_in_coordinates_lng));
        } else if (entry.clockInCoordinatesLng !== undefined && entry.clockInCoordinatesLng !== null) {
          lng = parseFloat(String(entry.clockInCoordinatesLng));
        } else if (entry.coordinates && entry.coordinates.lng !== undefined && entry.coordinates.lng !== null) {
          lng = parseFloat(String(entry.coordinates.lng));
        }
        
        // If coordinates are NaN, set them to 0
        if (isNaN(lat)) lat = 0;
        if (isNaN(lng)) lng = 0;
        
        // Use the timestamp from the selected entry (which is guaranteed to be the most recent)
        const lastUpdated = entry.timestamp;
        
        return {
          userId,
          firstName: entry.user_first_name || entry.userFirstName || user.firstName || 'Unknown',
          lastName: entry.user_last_name || entry.userLastName || user.lastName || 'User',
          coordinates: { lat, lng },
          lastUpdated
        };
      });
      
      console.log('Mapped users:', mappedUsers);
      setActiveUsers(mappedUsers);
    } catch (err: any) {
      console.error('Error fetching active users:', err);
      setMapError('Failed to load active users. Please try again later.');
    } finally {
      setMapLoading(false);
    }
  }, []);

  // Function to fetch business permissions for HR module access
  const fetchBusinessPermissions = useCallback(async () => {
    try {
      setPermissionLoading(true);
      setPermissionError(null);
      
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');
      
      console.log('=== HR Permission Check Debug ===');
      console.log('Token exists:', !!token);
      console.log('Business ID:', businessId);
      
      if (!token || !businessId) {
        console.warn('Missing token or business ID for HR permissions check');
        setAllowHrModule(false);
        return;
      }

      console.log('Making API call to:', `${API_BASE_URL}/businesses/${businessId}`);
      const response = await axios.get(`${API_BASE_URL}/businesses/${businessId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log('Full API Response:', response);
      console.log('Response Data:', response.data);
      console.log('Response Status:', response.status);

      // Check the direct response data (not wrapped in success/data structure)
      const businessData = response.data;
      console.log('Business Data:', businessData);
      console.log('allow_hr_module value:', businessData.allow_hr_module);
      console.log('allow_hr_module type:', typeof businessData.allow_hr_module);

      // Check if allow_hr_module is explicitly set to 1 (true in database)
      const isHrEnabled = businessData.allow_hr_module === 1;
      console.log('Setting allowHrModule to:', isHrEnabled);
      setAllowHrModule(isHrEnabled);
    } catch (err: any) {
      console.error('Error fetching business permissions:', err);
      console.error('Error response:', err.response);
      setPermissionError('Failed to verify HR module permissions');
      setAllowHrModule(false); // Default to false for security
    } finally {
      setPermissionLoading(false);
    }
  }, []);

  // Check business permissions on component mount
  useEffect(() => {
    fetchBusinessPermissions();
  }, [fetchBusinessPermissions]);

  useEffect(() => {
    if (tabValue === 0) {
      fetchStaff();
    } else if (tabValue === 1) {
      fetchShifts();
      fetchTeams();
      fetchLocations();
    } else if (tabValue === 2) {
      fetchTimeEntries();
    } else if (tabValue === 3) {
      fetchActiveUsers();
    }
  }, [tabValue, fetchShifts, fetchLocations, fetchTimeEntries, fetchActiveUsers]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = staff.filter(user => {
        const fullName = `${user.firstName || user.first_name} ${user.lastName || user.last_name}`.toLowerCase();
        return fullName.includes(searchTerm.toLowerCase()) || 
               user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
               user.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               user.locationName?.toLowerCase().includes(searchTerm.toLowerCase());
      });
      setFilteredStaff(filtered);
    } else {
      setFilteredStaff(staff);
    }
  }, [searchTerm, staff]);

  // Extract unique departments from staff data
  useEffect(() => {
    if (staff.length > 0) {
      const uniqueDepartments = Array.from(
        new Set(
          staff
            .map(user => user.department || user.locationName || '')
            .filter(dept => dept && dept.trim() !== '')
        )
      );
      setDepartments(uniqueDepartments as string[]);
    }
  }, [staff]);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/users`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Transform the data to fit our User interface
      const transformedUsers = response.data.map((user: any) => ({
        ...user,
        id: user.userId, // Keep id for backward compatibility
        first_name: user.firstName, // Keep first_name for backward compatibility
        last_name: user.lastName,   // Keep last_name for backward compatibility
        job_title: user.role || 'Staff Member', // Use role as job title if available
        department: user.locationName || 'General', // Use location as department if available
        status: user.isActive ? 'active' : 'inactive'
      }));
      
      setStaff(transformedUsers);
      setFilteredStaff(transformedUsers);
    } catch (err: any) {
      console.error('Error fetching staff:', err);
      setError('Failed to load staff. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/business-groups/my-teams`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setTeams(response.data);

      // Fetch members for each team
      const teamMembersData: Record<string, TeamMember[]> = {};
      for (const team of response.data) {
        const membersResponse = await axios.get(`${API_BASE_URL}/business-groups/${team.groupId}/members`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        teamMembersData[team.groupId] = membersResponse.data;
      }
      
      setTeamMembers(teamMembersData);
    } catch (err: any) {
      console.error('Error fetching teams:', err);
      setError('Failed to load teams. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  // Staff management functions
  const handleOpenStaffDialog = (user: User | null = null) => {
    if (user) {
      setSelectedUser(user);
      setFirstName(user.firstName || user.first_name || '');
      setLastName(user.lastName || user.last_name || '');
      setEmail(user.email);
      setJobTitle(user.job_title || user.role || '');
      setPhoneNumber(user.phone_number || '');
      setDepartment(user.department || user.locationName || '');
      setStatus(user.status || (user.isActive ? 'active' : 'inactive'));
    } else {
      setSelectedUser(null);
      setFirstName('');
      setLastName('');
      setEmail('');
      setJobTitle('');
      setPhoneNumber('');
      setDepartment('');
      setStatus('active');
    }
    setOpenStaffDialog(true);
  };

  const handleCloseStaffDialog = () => {
    setOpenStaffDialog(false);
  };

  const handleSaveStaff = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }

      const userData = {
        first_name: firstName,
        last_name: lastName,
        email: email,
        job_title: jobTitle,
        phone_number: phoneNumber,
        department: department,
        status: status
      };

      if (selectedUser) {
        // Update existing user
        await axios.put(`${API_BASE_URL}/users/${selectedUser.id}`, userData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setSuccess('Staff member updated successfully!');
      } else {
        // Create new user
        await axios.post(`${API_BASE_URL}/users`, userData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setSuccess('Staff member added successfully!');
      }
      
      // Refresh staff list
      fetchStaff();
      handleCloseStaffDialog();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error saving staff:', err);
      setError('Failed to save staff member. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStaff = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) {
      return;
    }
    
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }

      await axios.delete(`${API_BASE_URL}/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setSuccess('Staff member deleted successfully!');
      
      // Refresh staff list
      fetchStaff();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error deleting staff:', err);
      setError('Failed to delete staff member. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Rota scheduling functions
  const handleOpenShiftDialog = (shift: Shift | null = null) => {
    if (shift) {
      setSelectedShift(shift);
      setShiftUserId(shift.user_id);
      setShiftStartTime(new Date(shift.start_time));
      setShiftEndTime(new Date(shift.end_time));
      setShiftLocation(shift.location_id || '');  // Use location_id instead of location
      setShiftNotes(shift.notes || '');
    } else {
      setSelectedShift(null);
      setShiftUserId('');
      const now = new Date();
      now.setHours(9, 0, 0, 0);
      const end = new Date();
      end.setHours(17, 0, 0, 0);
      setShiftStartTime(now);
      setShiftEndTime(end);
      setShiftLocation('');  // Default to empty string
      setShiftNotes('');
    }
    setOpenShiftDialog(true);
  };

  const handleCloseShiftDialog = () => {
    setOpenShiftDialog(false);
  };

  const handleSaveShift = async () => {
    try {
      setLoading(true);
      
      if (!shiftUserId || !shiftStartTime || !shiftEndTime || !shiftLocation) {
        setError('Please fill all required fields');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }

      const shiftData = {
        user_ids: [shiftUserId],
        start_time: shiftStartTime.toISOString(),
        end_time: shiftEndTime.toISOString(),
        location_id: shiftLocation,
        notes: shiftNotes
      };

      if (selectedShift) {
        // Update existing shift
        await axios.put(`${API_BASE_URL}/shifts/${selectedShift.id}`, shiftData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setSuccess('Shift updated successfully!');
      } else {
        // Create new shift
        await axios.post(`${API_BASE_URL}/shifts`, shiftData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setSuccess('Shift added successfully!');
      }
      
      // Refresh shifts list
      fetchShifts();
      handleCloseShiftDialog();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error saving shift:', err);
      setError('Failed to save shift. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteShift = async (shiftId: string) => {
    if (!window.confirm('Are you sure you want to delete this shift?')) {
      return;
    }
    
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }

      await axios.delete(`${API_BASE_URL}/shifts/${shiftId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setSuccess('Shift deleted successfully!');
      
      // Refresh shifts
      fetchShifts();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error deleting shift:', err);
      setError('Failed to delete shift. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const renderWeekDays = () => {
    const start = startOfWeek(selectedWeek);
    const end = endOfWeek(selectedWeek);
    const days = eachDayOfInterval({ start, end });
    
    return days.map(day => format(day, 'EEE, dd/MM'));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getShiftsForDay = (day: Date) => {
    return shifts.filter(shift => {
      const shiftStart = new Date(shift.start_time);
      const dayStart = new Date(day.setHours(0, 0, 0, 0));
      const dayEnd = new Date(day.setHours(23, 59, 59, 999));
      return shiftStart >= dayStart && shiftStart <= dayEnd;
    });
  };

  const getShiftsForUserAndWeek = (userId: string) => {
    const start = startOfWeek(selectedWeek);
    const end = endOfWeek(selectedWeek);
    
    return shifts.filter(shift => {
      const shiftStart = new Date(shift.start_time);
      
      // Check if user is assigned to this shift
      const isUserAssigned = shift.assignments?.some(
        assignment => assignment.user_id === userId
      ) || shift.user_id === userId;
      
      return isUserAssigned && isWithinInterval(shiftStart, { start, end });
    }).map(shift => ({
      ...shift,
      day: new Date(shift.start_time).getDay()
    }));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getUserById = (userId: string) => {
    return staff.find(user => user.userId === userId || user.id === userId);
  };

  const formatShiftTime = (time: string) => {
    return format(new Date(time), 'HH:mm');
  };

  // Function to calculate the duration between two dates in hours and minutes
  const calculateDuration = (startTime: Date, endTime: Date): string => {
    const durationMs = endTime.getTime() - startTime.getTime();
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  };

  // Check permissions first
  if (permissionLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show access denied if HR module is not allowed
  if (!allowHrModule) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            The HR module is not enabled for your business. Please contact your administrator to enable this feature.
          </Typography>
          {permissionError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {permissionError}
            </Alert>
          )}
          <Box sx={{ mt: 3 }}>
            <Button 
              variant="outlined" 
              onClick={() => window.history.back()}
            >
              Go Back
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  if (loading && !staff.length && !shifts.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)', p: 3 }}> 
        <CircularProgress />
  </Box>
);
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ width: '100%', mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="HR tabs"
            variant="fullWidth"
          >
            <Tab icon={<PersonIcon />} label="Staff Management" {...a11yProps(0)} />
            <Tab icon={<ScheduleIcon />} label="Rota Scheduling" {...a11yProps(1)} />
            <Tab icon={<AccessTimeIcon />} label="Entries" {...a11yProps(2)} />
            <Tab icon={<MapIcon />} label="Map" {...a11yProps(3)} />
          </Tabs>
        </Box>

        {/* Error and success messages */}
        {error && (
          <Alert severity="error" sx={{ m: 3 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ m: 3 }}>
            {success}
          </Alert>
        )}
        
        {/* Staff Management Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">Staff Directory</Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => handleOpenStaffDialog()}
            >
              Add HR Check
            </Button>
          </Box>
          
          <Box sx={{ display: 'flex', mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search by name, email, or department"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ maxWidth: 500 }}
            />
            <IconButton onClick={fetchStaff} sx={{ ml: 1 }}>
              <RefreshIcon />
            </IconButton>
          </Box>
          
          <Grid container spacing={3}>
            {filteredStaff.length > 0 ? (
              filteredStaff.map((user) => (
                <Grid item xs={12} sm={6} md={4} key={user.userId || user.id}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar 
                          src={user.profile_image} 
                          sx={{ 
                            width: 64, 
                            height: 64, 
                            bgcolor: 'primary.main',
                            fontSize: '1.5rem',
                            mr: 2
                          }}
                        >
                          {((user.firstName || user.first_name) && (user.lastName || user.last_name)) ? 
                            `${(user.firstName || user.first_name || '')[0]}${(user.lastName || user.last_name || '')[0]}` : 
                            <PersonIcon />
                          }
                        </Avatar>
                        <Box>
                          <Typography variant="h6">
                            {user.firstName || user.first_name} {user.lastName || user.last_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {user.job_title || user.role || 'No job title'}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Divider sx={{ mb: 2 }} />
                      
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Email:
                        </Typography>
                        <Typography variant="body1">
                          {user.email}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Department:
                        </Typography>
                        <Typography variant="body1">
                          {user.department || user.locationName || 'Not assigned'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Role:
                        </Typography>
                        <Typography variant="body1">
                          {user.role || 'Staff Member'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Phone:
                        </Typography>
                        <Typography variant="body1">
                          {user.phone_number || 'Not provided'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <IconButton 
                          color="primary"
                          onClick={() => handleOpenStaffDialog(user)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          color="error"
                          onClick={() => handleDeleteStaff(user.userId || user.id || '')}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography align="center">
                  {searchTerm ? 'No staff members found matching your search.' : 'No staff members found. Add one to get started.'}
                </Typography>
              </Grid>
            )}
          </Grid>
        </TabPanel>
        
        {/* Rota Scheduling Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">Rota Schedule</Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => handleOpenShiftDialog()}
            >
              Add Shift
            </Button>
          </Box>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Select Week Starting"
                  value={selectedWeek}
                  onChange={(newValue) => {
                    if (newValue) {
                      // Set to the start of the week for better consistency
                      const weekStartDay = startOfWeek(newValue);
                      setSelectedWeek(weekStartDay);
                      // Refetch shifts when week changes
                      fetchShifts();
                    }
                  }}
                  format="dd/MM/yyyy" // UK date format
                  slotProps={{ 
                    textField: { 
                      fullWidth: true,
                      helperText: `Week ${format(selectedWeek, "'w'w, yyyy")} - Showing ${format(startOfWeek(selectedWeek), "dd/MM")} to ${format(endOfWeek(selectedWeek), "dd/MM")}`
                    } 
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="filter-label">Filter By</InputLabel>
                <Select
                  labelId="filter-label"
                  value={selectedTeam !== 'all' ? 'team' : (selectedDepartment !== 'all' ? 'department' : 'all')}
                  label="Filter By"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'all') {
                      setSelectedDepartment('all');
                      setSelectedTeam('all');
                    }
                  }}
                >
                  <MenuItem value="all">All Staff</MenuItem>
                  <MenuItem value="department">By Department</MenuItem>
                  <MenuItem value="team">By Team</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              {selectedTeam !== 'all' || selectedDepartment !== 'all' ? (
                selectedTeam !== 'all' ? (
                  <FormControl fullWidth>
                    <InputLabel id="team-filter-label">Team</InputLabel>
                    <Select
                      labelId="team-filter-label"
                      value={selectedTeam}
                      label="Team"
                      onChange={(e) => {
                        setSelectedTeam(e.target.value);
                        setSelectedDepartment('all');
                      }}
                    >
                      <MenuItem value="all">All Teams</MenuItem>
                      {teams.map((team) => (
                        <MenuItem key={team.groupId} value={team.groupId}>
                          {team.groupName} ({team.memberCount} members)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <FormControl fullWidth>
                    <InputLabel id="department-filter-label">Department</InputLabel>
                    <Select
                      labelId="department-filter-label"
                      value={selectedDepartment}
                      label="Department"
                      onChange={(e) => {
                        setSelectedDepartment(e.target.value);
                        setSelectedTeam('all');
                      }}
                    >
                      <MenuItem value="all">All Departments</MenuItem>
                      {departments.map((dept) => (
                        <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )
              ) : (
                <Button 
                  startIcon={<RefreshIcon />} 
                  onClick={() => {
                    fetchStaff();
                    fetchShifts();
                    fetchTeams();
                  }} 
                  variant="outlined" 
                  sx={{ height: 56, width: '100%' }}
                >
                  Refresh Data
                </Button>
              )}
            </Grid>
          </Grid>
          
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table sx={{ minWidth: 650 }} aria-label="rota schedule">
              <TableHead>
                <TableRow>
                  <TableCell>Staff Member</TableCell>
                  {renderWeekDays().map((day, index) => (
                    <TableCell key={index}>{day}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {staff
                  .filter(user => {
                    if (selectedTeam !== 'all') {
                      // Check if user is in the selected team
                      const teamData = teamMembers[selectedTeam] || [];
                      return teamData.some(member => member.userId === (user.userId || user.id));
                    } else if (selectedDepartment !== 'all') {
                      // Check if user is in the selected department
                      return user.department === selectedDepartment || user.locationName === selectedDepartment;
                    }
                    return true; // Show all users if no filter is selected
                  })
                  .map((user) => {
                    const userShifts = getShiftsForUserAndWeek(user.userId || user.id || '');
                    return (
                      <TableRow key={user.userId || user.id}>
                        <TableCell component="th" scope="row">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar 
                              src={user.profile_image} 
                              sx={{ 
                                width: 32, 
                                height: 32, 
                                bgcolor: 'primary.main',
                                fontSize: '0.875rem',
                                mr: 1
                              }}
                            >
                              {(user.firstName || user.first_name || '') && (user.lastName || user.last_name || '') ? 
                                `${(user.firstName || user.first_name || '').charAt(0)}${(user.lastName || user.last_name || '').charAt(0)}` : 
                                <PersonIcon />
                              }
                            </Avatar>
                            <Box>
                              <Typography>
                                {user.firstName || user.first_name} {user.lastName || user.last_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {user.department || user.locationName || 'No Department'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        {Array.from({ length: 7 }).map((_, dayIndex) => {
                          const dayShifts = userShifts.filter(shift => shift.day === dayIndex);
                          return (
                            <TableCell key={dayIndex}>
                              {dayShifts.length > 0 ? (
                                <Box>
                                  {dayShifts.map((shift, index) => (
                                    <Tooltip 
                                      key={index} 
                                      title={
                                        <>
                                          <Typography variant="body2">{formatShiftTime(shift.start_time)} - {formatShiftTime(shift.end_time)}</Typography>
                                          <Typography variant="body2">Location: {shift.location}</Typography>
                                          {shift.notes && <Typography variant="body2">Notes: {shift.notes}</Typography>}
                                          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-around' }}>
                                            <Button size="small" onClick={() => handleOpenShiftDialog(shift)}>Edit</Button>
                                            <Button size="small" color="error" onClick={() => handleDeleteShift(shift.id)}>Delete</Button>
                                          </Box>
                                        </>
                                      }
                                    >
                                      <Box 
                                        sx={{ 
                                          bgcolor: 'primary.light', 
                                          color: 'primary.contrastText',
                                          p: 1,
                                          borderRadius: 1,
                                          mb: index < dayShifts.length - 1 ? 1 : 0,
                                          cursor: 'pointer'
                                        }}
                                      >
                                        {formatShiftTime(shift.start_time)} - {formatShiftTime(shift.end_time)}
                                      </Box>
                                    </Tooltip>
                                  ))}
                                </Box>
                              ) : (
                                <IconButton 
                                  size="small" 
                                  onClick={() => {
                                    const day = addDays(startOfWeek(selectedWeek), dayIndex);
                                    const startTime = new Date(day);
                                    startTime.setHours(9, 0, 0, 0);
                                    const endTime = new Date(day);
                                    endTime.setHours(17, 0, 0, 0);
                                    
                                    setSelectedShift(null);
                                    setShiftUserId(user.userId || '');
                                    setShiftStartTime(startTime);
                                    setShiftEndTime(endTime);
                                    setShiftLocation('');
                                    setShiftNotes('');
                                    setOpenShiftDialog(true);
                                  }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
        
        {/* Time Entries Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">Time Entries</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={timeEntryDateRange.startDate}
                  onChange={(newValue) => {
                    if (newValue) {
                      setTimeEntryDateRange(prev => ({
                        ...prev,
                        startDate: newValue
                      }));
                    }
                  }}
                  format="dd/MM/yyyy" // UK date format
                  slotProps={{ 
                    textField: { 
                      size: "small",
                      sx: { width: 160 }
                    } 
                  }}
                />
              </LocalizationProvider>
              
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={timeEntryDateRange.endDate}
                  onChange={(newValue) => {
                    if (newValue) {
                      setTimeEntryDateRange(prev => ({
                        ...prev,
                        endDate: newValue
                      }));
                    }
                  }}
                  format="dd/MM/yyyy" // UK date format
                  slotProps={{ 
                    textField: { 
                      size: "small",
                      sx: { width: 160 }
                    } 
                  }}
                />
              </LocalizationProvider>
              
              <Button 
                variant="outlined" 
                startIcon={<RefreshIcon />}
                onClick={fetchTimeEntries}
              >
                Refresh
              </Button>
            </Box>
          </Box>
          
          {timeEntriesError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {timeEntriesError}
            </Alert>
          )}
          
          {timeEntriesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Staff</TableCell>
                    <TableCell>Clock In</TableCell>
                    <TableCell>Clock In Location</TableCell>
                    <TableCell>Clock Out</TableCell>
                    <TableCell>Clock Out Location</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timeEntries.length > 0 ? (
                    timeEntries.map((entry) => {
                      // Calculate duration if both clock in and clock out exist
                      const duration = entry.clock_in && entry.clock_out 
                        ? calculateDuration(new Date(entry.clock_in), new Date(entry.clock_out))
                        : null;
                      
                      return (
                        <TableRow key={entry.entry_id}>
                          <TableCell>
                            {entry.user_first_name && entry.user_last_name ? 
                              `${entry.user_first_name} ${entry.user_last_name}` : 
                              'Unknown User'}
                            <Typography variant="caption" display="block" color="text.secondary">
                              {entry.user_email || ''}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {entry.clock_in ? (
                              format(new Date(entry.clock_in), 'dd/MM/yyyy HH:mm')
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>
                            <Tooltip title={entry.clock_in_coordinates_lat && entry.clock_in_coordinates_lng ? 
                              `Lat: ${parseFloat(String(entry.clock_in_coordinates_lat)).toFixed(6)}, Lng: ${parseFloat(String(entry.clock_in_coordinates_lng)).toFixed(6)}` : 
                              'No coordinates'}>
                              <span>{entry.clock_in_location || '-'}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            {entry.clock_out ? (
                              format(new Date(entry.clock_out), 'dd/MM/yyyy HH:mm')
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>
                            <Tooltip title={entry.clock_out_coordinates_lat && entry.clock_out_coordinates_lng ? 
                              `Lat: ${parseFloat(String(entry.clock_out_coordinates_lat)).toFixed(6)}, Lng: ${parseFloat(String(entry.clock_out_coordinates_lng)).toFixed(6)}` : 
                              'No coordinates'}>
                              <span>{entry.clock_out_location || '-'}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            {duration || '-'}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ 
                              backgroundColor: entry.status === 'active' ? 'success.light' : 'text.disabled',
                              color: entry.status === 'active' ? 'success.contrastText' : 'common.white',
                              borderRadius: 1,
                              padding: '4px 8px',
                              display: 'inline-block',
                              fontSize: '0.75rem',
                              fontWeight: 'medium',
                              textTransform: 'capitalize'
                            }}>
                              {entry.status}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {entry.notes || '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No time entries found for the selected date range.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
        
        {/* Map Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">Staff Location Map</Typography>
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />}
              onClick={fetchActiveUsers}
            >
              Refresh
            </Button>
          </Box>
          
          {mapError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {mapError}
            </Alert>
          )}
          
          {activeUsers.length === 0 && !mapLoading && (
            <Alert severity="info" sx={{ mb: 3 }}>
              No staff members are currently clocked in.
            </Alert>
          )}
          
          {mapLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ mb: 3 }}>
              <div style={{ 
                height: '500px', 
                width: '100%', 
                marginBottom: '1.5rem',
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <LocationMap locations={activeUsers.map(user => {
                  // Convert active users to the format LocationMap expects
                  let lat = 0;
                  let lng = 0;
                  
                  try {
                    // Handle lat coordinate
                    if (typeof user.coordinates.lat === 'string') {
                      lat = parseFloat(user.coordinates.lat);
                    } else if (typeof user.coordinates.lat === 'number') {
                      lat = user.coordinates.lat;
                    }
                    
                    // Handle lng coordinate
                    if (typeof user.coordinates.lng === 'string') {
                      lng = parseFloat(user.coordinates.lng);
                    } else if (typeof user.coordinates.lng === 'number') {
                      lng = user.coordinates.lng;
                    }
                    
                    // Check for invalid values
                    if (isNaN(lat)) lat = 0;
                    if (isNaN(lng)) lng = 0;
                  } catch (e) {
                    console.error('Error parsing coordinates:', e);
                    lat = 0;
                    lng = 0;
                  }
                  
                  return {
                    userId: user.userId,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    lastUpdated: user.lastUpdated,
                    validCoordinates: {
                      lat,
                      lng,
                      isValid: lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng)
                    },
                    locationType: 'clockIn' as const
                  };
                }).filter(user => user.validCoordinates.isValid)} />
              </div>
            </Box>
          )}
          
          {/* Active Staff List */}
          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Last Updated Active Staff
          </Typography>
          
          {activeUsers.length > 0 ? (
            <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Staff Member</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Last Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeUsers.map((user, index) => (
                    <TableRow key={`active-user-${user.userId}-${index}`}>
                      <TableCell>
                        {user.firstName} {user.lastName}
                      </TableCell>
                      <TableCell>
                        {user.coordinates.lat !== 0 && user.coordinates.lng !== 0 ? (
                          `${parseFloat(String(user.coordinates.lat)).toFixed(6)}, ${parseFloat(String(user.coordinates.lng)).toFixed(6)}`
                        ) : (
                          'Unknown location'
                        )}
                      </TableCell>
                      <TableCell>
                        {format(new Date(user.lastUpdated), 'dd/MM/yyyy HH:mm')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">No active staff members found.</Alert>
          )}
        </TabPanel>
      </Paper>
      
      {/* Staff Edit Dialog */}
      <Dialog open={openStaffDialog} onClose={handleCloseStaffDialog} maxWidth="md" fullWidth>
        <DialogTitle>{selectedUser ? 'Edit Staff Member' : 'Add New Staff Member'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Job Title"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Department"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  value={status}
                  label="Status"
                  onChange={(e) => setStatus(e.target.value as 'active' | 'inactive' | 'on_leave')}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="on_leave">On Leave</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStaffDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveStaff} 
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Shift Edit Dialog */}
      <Dialog open={openShiftDialog} onClose={handleCloseShiftDialog} maxWidth="md" fullWidth>
        <DialogTitle>{selectedShift ? 'Edit Shift' : 'Add New Shift'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="user-label">Staff Member</InputLabel>
                <Select
                  labelId="user-label"
                  value={shiftUserId}
                  label="Staff Member"
                  onChange={(e) => setShiftUserId(e.target.value)}
                  required
                >
                  {staff.map((user) => (
                    <MenuItem key={user.userId} value={user.userId}>
                      {(user.firstName || user.first_name || '') + ' ' + (user.lastName || user.last_name || '')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Start Time"
                  value={shiftStartTime}
                  onChange={(newValue) => {
                    if (newValue) {
                      setShiftStartTime(newValue);
                    }
                  }}
                  format="dd/MM/yyyy HH:mm" // UK format with time
                  slotProps={{ 
                    textField: { 
                      fullWidth: true, 
                      required: true,
                      InputProps: {
                        startAdornment: <div style={{marginRight: '8px'}}><ScheduleIcon color="primary" /></div>
                      }
                    }
                  }}
                  views={['year', 'month', 'day']} // Limit to date selection
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="End Time"
                  value={shiftEndTime}
                  onChange={(newValue) => {
                    if (newValue) {
                      setShiftEndTime(newValue);
                    }
                  }}
                  format="dd/MM/yyyy HH:mm" // UK format with time
                  slotProps={{ 
                    textField: { 
                      fullWidth: true, 
                      required: true,
                      InputProps: {
                        startAdornment: <div style={{marginRight: '8px'}}><ScheduleIcon color="primary" /></div>
                      }
                    }
                  }}
                  views={['year', 'month', 'day']} // Limit to date selection
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="location-label">Location</InputLabel>
                <Select
                  labelId="location-label"
                  value={shiftLocation}
                  label="Location"
                  onChange={(e) => setShiftLocation(e.target.value)}
                  required
                >
                  {locations.map((location) => (
                    <MenuItem key={location.locationId} value={location.locationId}>
                      {location.locationName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={shiftNotes}
                onChange={(e) => setShiftNotes(e.target.value)}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseShiftDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveShift} 
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HR; 